/**
 * Shared helper for determining integration connected state using connection_status field.
 */

export type ConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'auth_failed'
  | 'partial_scopes';

/**
 * Determines whether an integration is connected.
 *
 * If `connectionStatus` is present and non-empty, it is the source of truth.
 * Otherwise falls back to `fallbackConnected` for backwards compatibility with
 * older API responses that only return a `connected` boolean.
 *
 * `partial_scopes` is treated as connected — the OAuth grant exists, just with
 * a reduced scope set. Callers that need to distinguish full vs partial grants
 * use `isIntegrationLimited` for the visual treatment.
 */
export function isIntegrationConnected(
  connectionStatus: string | undefined | null,
  fallbackConnected: boolean
): boolean {
  if (connectionStatus == null || connectionStatus === '') {
    return fallbackConnected;
  }
  return (
    connectionStatus === 'connected' || connectionStatus === 'partial_scopes'
  );
}

/**
 * Returns true when the integration has a partial-scope OAuth grant —
 * the account is connected but the user did not grant all requested
 * Google permissions (e.g. checked Gmail but not Calendar in the consent
 * screen). Callers use this to render the "Limited permissions" UI.
 */
export function isIntegrationLimited(
  connectionStatus: string | undefined | null
): boolean {
  return connectionStatus === 'partial_scopes';
}
